import { useState } from 'react';

interface ShareOptions {
  eventId: string;
  eventTitle?: string;
}

interface ShareResult {
  eventId: string;
  shortCode: string;
  shortUrl: string;
  fullUrl: string;
}

export const useEventShare = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getShortUrl = async (eventId: string): Promise<ShareResult | null> => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch(`/e/short-url/${eventId}`);
      
      if (!response.ok) {
        throw new Error('Failed to generate short URL');
      }

      const data: ShareResult = await response.json();
      return data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to generate short URL';
      setError(errorMessage);
      console.error('Error generating short URL:', err);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const copyLink = async ({ eventId }: ShareOptions): Promise<boolean> => {
    try {
      setIsLoading(true);
      setError(null);

      const result = await getShortUrl(eventId);
      
      if (!result) {
        throw new Error('Failed to generate short URL');
      }

      await navigator.clipboard.writeText(result.shortUrl);
      
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to copy link';
      setError(errorMessage);
      console.error('Error copying link:', err);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const shareToFacebook = async ({ eventId }: ShareOptions): Promise<boolean> => {
    try {
      setIsLoading(true);
      setError(null);

      const result = await getShortUrl(eventId);
      
      if (!result) {
        throw new Error('Failed to generate short URL');
      }

      // Add UTM parameters for Facebook tracking
      const urlWithUtm = `${result.shortUrl}?utm_source=facebook&utm_medium=social&utm_campaign=event_share`;
      const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(urlWithUtm)}`;
      window.open(facebookUrl, '_blank', 'width=600,height=400');
      
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to share to Facebook';
      setError(errorMessage);
      console.error('Error sharing to Facebook:', err);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const shareToLinkedIn = async ({ eventId }: ShareOptions): Promise<boolean> => {
    try {
      setIsLoading(true);
      setError(null);

      const result = await getShortUrl(eventId);
      
      if (!result) {
        throw new Error('Failed to generate short URL');
      }

      // Add UTM parameters for LinkedIn tracking
      const urlWithUtm = `${result.shortUrl}?utm_source=linkedin&utm_medium=social&utm_campaign=event_share`;
      const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(urlWithUtm)}`;
      window.open(linkedInUrl, '_blank', 'width=600,height=400');
      
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to share to LinkedIn';
      setError(errorMessage);
      console.error('Error sharing to LinkedIn:', err);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const shareToX = async ({ eventId, eventTitle }: ShareOptions): Promise<boolean> => {
    try {
      setIsLoading(true);
      setError(null);

      const result = await getShortUrl(eventId);
      
      if (!result) {
        throw new Error('Failed to generate short URL');
      }

      // Add UTM parameters for X/Twitter tracking
      const urlWithUtm = `${result.shortUrl}?utm_source=twitter&utm_medium=social&utm_campaign=event_share`;
      const text = eventTitle ? `Check out ${eventTitle}!` : 'Check out this event!';
      const xUrl = `https://x.com/intent/tweet?url=${encodeURIComponent(urlWithUtm)}&text=${encodeURIComponent(text)}`;
      window.open(xUrl, '_blank', 'width=600,height=400');
      
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to share to X';
      setError(errorMessage);
      console.error('Error sharing to X:', err);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Instagram doesn't support direct URL sharing from web
  // Users need to copy the link and paste in Instagram app
  const shareToInstagram = async ({ eventId }: ShareOptions): Promise<boolean> => {
    try {
      setIsLoading(true);
      setError(null);

      const result = await getShortUrl(eventId);
      
      if (!result) {
        throw new Error('Failed to generate short URL');
      }

      // Add UTM parameters for Instagram tracking
      const urlWithUtm = `${result.shortUrl}?utm_source=instagram&utm_medium=social&utm_campaign=event_share`;
      
      // Copy to clipboard for Instagram
      await navigator.clipboard.writeText(urlWithUtm);
      
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to prepare Instagram share';
      setError(errorMessage);
      console.error('Error preparing Instagram share:', err);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    copyLink,
    shareToFacebook,
    shareToLinkedIn,
    shareToX,
    shareToInstagram,
    getShortUrl,
    isLoading,
    error,
  };
};
